import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useTerritoryGateOpen,
  useTerritoryGateReason,
  useTerritoryGateCategory,
  useCloseTerritoryGate,
} from '@/store/territoryGateStore';
import type { TerritoryBlockReason } from '@/lib/territory/types';

/**
 * Territory block screen. Mount once in the app shell. Driven by the territory
 * gate store: opened by the 403 TERRITORY_BLOCKED interceptor and proactively by
 * the gated entry points (deposit / trade / SU-completion). Dismissable — a
 * blocked user can still browse the platform; this only blocks the gated action.
 *
 * Copy is keyed off the block reason. Match the visuals to the Figma block
 * screen (node 3216-95567) when finalising.
 */
export function TerritoryBlockGate() {
  const open = useTerritoryGateOpen();
  const reason = useTerritoryGateReason();
  const category = useTerritoryGateCategory();
  const close = useCloseTerritoryGate();

  if (!open) return null;

  const { title, body } = copyFor(reason, category);

  return (
    <Dialog open onClose={close} size="md">
      <div className="flex flex-col gap-4 text-center">
        <h2 className="text-lg font-semibold text-primary">{title}</h2>
        <p className="text-sm text-muted">{body}</p>
        <Button onClick={close} className="w-full">
          Got it
        </Button>
      </div>
    </Dialog>
  );
}

function copyFor(reason: TerritoryBlockReason | null, category: string | null): { title: string; body: string } {
  switch (reason) {
    case 'UNRESOLVED':
      return {
        title: 'Verification required',
        body: 'Finish verifying your identity and address to continue. Once verified, we’ll confirm availability in your region.',
      };
    case 'CATEGORY_BLOCKED':
      return {
        title: 'Not available in your region',
        body: category
          ? `The “${category}” category isn’t available where you are. You can still browse and trade other categories.`
          : 'This category isn’t available where you are. You can still browse and trade other categories.',
      };
    case 'COUNTRY_NOT_OPENED':
    case 'COUNTRY_INACTIVE':
    case 'STATE_INACTIVE':
    default:
      return {
        title: 'Not available in your region',
        body: 'Trading and deposits aren’t available from your location yet. You’re welcome to keep browsing the markets.',
      };
  }
}
